import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Dice5, Timer, Sparkles, Layers, Trophy, X, Zap, Award, Lock, Crown } from "lucide-react";
import { GufoIcon } from "@/components/GufoIcon";
import GironeBadge from "@/components/GironeBadge";

export default function Fantascommesse() {
  const { user, refresh } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [bets, setBets] = useState([]);
  const [betOn, setBetOn] = useState(null);
  const [stake, setStake] = useState(1);
  const [busy, setBusy] = useState(false);

  // Combo cart: [{ market_id, market_title, option_id, option_label, odds, matchday }]
  const [comboMode, setComboMode] = useState(false);
  const [comboCart, setComboCart] = useState([]);
  const [comboStake, setComboStake] = useState(5);
  const [comboBusy, setComboBusy] = useState(false);

  // Cash Rush
  const [rushPeriod, setRushPeriod] = useState("week");
  const [ranking, setRanking] = useState([]);

  // Achievements
  const [achievements, setAchievements] = useState(null);

  const loadMarkets = useCallback(async () => {
    try { const { data } = await api.get("/public/markets"); setMarkets(data); } catch {}
  }, []);
  const loadBets = useCallback(async () => {
    if (!user?.id) return setBets([]);
    try { const { data } = await api.get("/me/bets"); setBets(data); } catch {}
  }, [user?.id]);
  const loadRanking = useCallback(async () => {
    try { const { data } = await api.get(`/public/cash-rush?period=${rushPeriod}`); setRanking(data.ranking || []); } catch {}
  }, [rushPeriod]);
  const loadAchievements = useCallback(async () => {
    if (!user?.id) return setAchievements(null);
    try { const { data } = await api.get("/me/achievements"); setAchievements(data); } catch {}
  }, [user?.id]);

  useEffect(() => { loadMarkets(); }, [loadMarkets]);
  useEffect(() => { loadBets(); }, [loadBets]);
  useEffect(() => { loadRanking(); }, [loadRanking]);
  useEffect(() => { loadAchievements(); }, [loadAchievements]);

  const openMarkets = markets.filter((m) => m.status === "open");

  const submitBet = async () => {
    if (!user?.id) return toast.error("Accedi per scommettere");
    if (!betOn) return;
    if (stake < 1) return toast.error("Puntata minima 1 Gufo");
    setBusy(true);
    try {
      const { data } = await api.post("/me/bets", {
        market_id: betOn.market.id,
        option_id: betOn.option.id,
        stake: parseInt(stake, 10),
      });
      toast.success(`Puntata registrata!`, { description: `Wallet: ${data.gufi_wallet} Gufi` });
      setBetOn(null); setStake(1);
      await Promise.all([loadBets(), loadMarkets(), refresh()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore puntata");
    } finally { setBusy(false); }
  };

  const cashout = async (bet) => {
    if (!window.confirm("Cashout: recuperi l'80% della puntata. Confermi?")) return;
    try {
      const { data } = await api.post(`/me/bets/${bet.id}/cashout`, {});
      toast.success(`Cashout eseguito: +${data.refund} Gufi`);
      await Promise.all([loadBets(), refresh()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cashout fallito");
    }
  };

  const toggleComboLeg = (market, option) => {
    setComboCart((cart) => {
      const existingIdx = cart.findIndex((l) => l.market_id === market.id);
      if (existingIdx >= 0) {
        if (cart[existingIdx].option_id === option.id) {
          // Toggle off
          return cart.filter((_, i) => i !== existingIdx);
        }
        // Replace option for this market
        return cart.map((l, i) => i === existingIdx
          ? { ...l, option_id: option.id, option_label: option.label, odds: option.odds }
          : l);
      }
      return [...cart, {
        market_id: market.id,
        market_title: market.title,
        matchday: market.matchday,
        option_id: option.id,
        option_label: option.label,
        odds: option.odds,
      }];
    });
  };

  const removeComboLeg = (mid) => setComboCart((c) => c.filter((l) => l.market_id !== mid));

  const combinedOdds = useMemo(
    () => comboCart.reduce((acc, l) => acc * (l.odds || 1), 1),
    [comboCart]
  );
  const comboWin = Math.round(comboStake * combinedOdds);

  // Deep-link: /fantascommesse?tab=trofei seleziona il tab richiesto
  const [params] = useSearchParams();
  const initialTab = (() => {
    const t = params.get("tab");
    return ["live", "my", "cashrush", "trofei"].includes(t) ? t : "live";
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const t = params.get("tab");
    if (t && ["live", "my", "cashrush", "trofei"].includes(t)) setActiveTab(t);
  }, [params]);

  const submitCombo = async () => {
    if (!user?.id) return toast.error("Accedi per scommettere");
    if (comboCart.length < 2) return toast.error("Servono almeno 2 selezioni");
    if (comboStake < 1) return toast.error("Puntata minima 1 Gufo");
    setComboBusy(true);
    try {
      const { data } = await api.post("/me/bets/combo", {
        stake: parseInt(comboStake, 10),
        legs: comboCart.map((l) => ({ market_id: l.market_id, option_id: l.option_id })),
      });
      toast.success(`Combo x${data.bet.combined_odds} registrata!`, { description: `Wallet: ${data.gufi_wallet} Gufi` });
      setComboCart([]); setComboStake(5); setComboMode(false);
      await Promise.all([loadBets(), loadMarkets(), refresh()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore combo");
    } finally { setComboBusy(false); }
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div className="fade-up flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(139,92,246,0.2)", color: "var(--scommetti)" }}>
            <Dice5 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-slate-500">Sala scommesse FIFE</div>
            <h1 className="display font-black text-4xl md:text-5xl">Fantascommesse</h1>
          </div>
          {user?.id && (
            <div className="glass rounded-full px-4 py-2 inline-flex items-center gap-2">
              <GufoIcon size={22} />
              <span className="display font-black" style={{ color: "var(--brand)" }}>{user.gufi_wallet ?? 0}</span>
              <span className="text-xs text-slate-500 uppercase">nel wallet</span>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-50 border border-slate-200 w-full grid grid-cols-2 h-auto sm:w-auto sm:inline-flex sm:grid-cols-none gap-1 p-1">
            <TabsTrigger value="live" data-testid="tab-live" className="text-[11px] sm:text-sm py-2">
              Live ({openMarkets.length})
            </TabsTrigger>
            {user?.id && (
              <TabsTrigger value="my" data-testid="tab-my" className="text-[11px] sm:text-sm py-2">
                Le mie ({bets.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="cashrush" data-testid="tab-cashrush" className="text-[11px] sm:text-sm py-2">
              <Trophy className="w-3 h-3 mr-1" /> Cash Rush
            </TabsTrigger>
            {user?.id && (
              <TabsTrigger value="trofei" data-testid="tab-trofei" className="text-[11px] sm:text-sm py-2">
                <Award className="w-3 h-3 mr-1" /> Trofei ({achievements?.unlocked_count ?? 0})
              </TabsTrigger>
            )}
          </TabsList>

          {/* Live */}
          <TabsContent value="live" className="mt-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="text-sm text-slate-600">
                {comboMode ? (
                  <span><b>Modalità Combo:</b> tocca le opzioni per aggiungere selezioni (max 8, quote moltiplicate)</span>
                ) : (
                  <span>Tocca la quota per piazzare la scommessa singola.</span>
                )}
              </div>
              <Button
                onClick={() => { setComboMode(!comboMode); if (comboMode) setComboCart([]); }}
                variant={comboMode ? "default" : "outline"}
                className={comboMode ? "btn-brand rounded-full" : "border-slate-300 rounded-full"}
                data-testid="combo-mode-toggle">
                <Layers className="w-4 h-4 mr-1" />
                {comboMode ? `Combo attiva (${comboCart.length})` : "Modalità Combo"}
              </Button>
            </div>
            {openMarkets.length === 0 ? (
              <Empty text="Nessun mercato aperto. L'Admin apre le scommesse prima di ogni giornata." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger">
                {openMarkets.map((m) => (
                  <MarketCard
                    key={m.id}
                    market={m}
                    comboMode={comboMode}
                    comboSelected={comboCart.find((l) => l.market_id === m.id)?.option_id}
                    onBet={(opt) => {
                      if (comboMode) return toggleComboLeg(m, opt);
                      setBetOn({ market: m, option: opt }); setStake(1);
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* My bets */}
          {user?.id && (
            <TabsContent value="my" className="mt-6">
              {bets.length === 0 ? (
                <Empty text="Nessuna puntata ancora." />
              ) : (
                <div className="card-surface rounded-2xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                      <tr>
                        <th className="px-3 py-3 text-left">Mercato</th>
                        <th className="px-3 py-3 text-left">Scelta</th>
                        <th className="px-3 py-3 text-center">Quota</th>
                        <th className="px-3 py-3 text-center">Puntata</th>
                        <th className="px-3 py-3 text-center">Esito</th>
                        <th className="px-3 py-3 text-center">Vincita</th>
                        <th className="px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bets.map((b) => (
                        <BetRow key={b.id} bet={b} onCashout={cashout} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          )}

          {/* Cash Rush */}
          <TabsContent value="cashrush" className="mt-6">
            <CashRushPanel
              ranking={ranking}
              period={rushPeriod}
              setPeriod={setRushPeriod}
              me={user?.id}
            />
          </TabsContent>

          {/* Trofei / Achievements */}
          {user?.id && (
            <TabsContent value="trofei" className="mt-6">
              <AchievementsPanel data={achievements} meId={user?.id} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Combo Cart floating panel */}
      {comboMode && comboCart.length > 0 && (
        <div className="fixed left-3 right-3 z-40 md:left-auto md:right-6 md:w-96"
             style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
             data-testid="combo-cart">
          <div className="bg-white rounded-2xl shadow-xl border-2 border-[var(--scommetti)]/50 p-4">
            <div className="flex items-center justify-between">
              <div className="display font-black text-lg flex items-center gap-2">
                <Layers className="w-5 h-5" style={{ color: "var(--scommetti)" }} />
                Combo ({comboCart.length})
              </div>
              <button onClick={() => setComboCart([])} className="text-slate-400 hover:text-slate-700" data-testid="combo-clear">
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="mt-3 space-y-1 max-h-40 overflow-y-auto">
              {comboCart.map((l) => (
                <li key={l.market_id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-900 truncate font-semibold">{l.market_title}</div>
                    <div className="text-slate-500 truncate">{l.option_label}</div>
                  </div>
                  <div className="display font-black" style={{ color: "var(--scommetti)" }}>x{l.odds}</div>
                  <button onClick={() => removeComboLeg(l.market_id)} className="ml-2 text-slate-300 hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-3 rounded-xl bg-slate-50 p-2 flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-slate-500">Quota totale</span>
              <span className="display font-black text-2xl" style={{ color: "var(--scommetti)" }}>
                x{combinedOdds.toFixed(2)}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Input type="number" min={1} value={comboStake}
                     onChange={(e) => setComboStake(parseInt(e.target.value || "1", 10))}
                     className="bg-slate-50 border-slate-200 flex-1" data-testid="combo-stake" />
              <div className="text-xs text-slate-600 whitespace-nowrap">
                Vinci <b style={{ color: "var(--brand)" }}>{comboWin}</b>
              </div>
            </div>
            <Button onClick={submitCombo} disabled={comboBusy || comboCart.length < 2}
                    className="btn-brand rounded-full mt-3 w-full" data-testid="combo-submit">
              {comboBusy ? "Invio..." : `Piazza combo (${comboCart.length} selez.)`}
            </Button>
          </div>
        </div>
      )}

      {/* Single Bet dialog */}
      <Dialog open={!!betOn} onOpenChange={(o) => !o && setBetOn(null)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900" data-testid="bet-dialog">
          {betOn && (
            <>
              <DialogHeader>
                <DialogTitle className="display font-black text-2xl">Piazza la puntata</DialogTitle>
                <DialogDescription className="text-slate-500">
                  <b>{betOn.market.title}</b> · Giornata {betOn.market.matchday}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-2xl p-4 border border-slate-200 bg-slate-50">
                  <div className="text-xs uppercase tracking-widest text-slate-500">La tua scelta</div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="display font-bold text-lg">{betOn.option.label}</div>
                    <div className="display font-black text-lg" style={{ color: "var(--scommetti)" }}>x{betOn.option.odds}</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Puntata (Gufi)</div>
                  <Input type="number" min={1} max={user?.gufi_wallet || 1} value={stake}
                         onChange={(e) => setStake(parseInt(e.target.value || "1", 10))}
                         className="bg-slate-50 border-slate-200" data-testid="bet-stake" />
                  <div className="text-xs text-slate-500 mt-1">Wallet: {user?.gufi_wallet ?? 0} Gufi</div>
                </div>
                <div className="rounded-xl p-3 flex items-center justify-between border" style={{ background: "rgba(204,255,0,0.05)", borderColor: "rgba(204,255,0,0.25)" }}>
                  <span className="text-sm text-slate-600">Vincita potenziale</span>
                  <span className="display font-black text-lg" style={{ color: "var(--brand)" }}>
                    {Math.round((stake || 0) * betOn.option.odds)} Gufi
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setBetOn(null)} className="border-slate-300 rounded-full">Annulla</Button>
                  <Button disabled={busy} onClick={submitBet} className="btn-brand rounded-full" data-testid="bet-submit">
                    {busy ? "Invio..." : "Conferma puntata"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}

function BetRow({ bet, onCashout }) {
  const isCombo = bet.type === "combo";
  return (
    <tr className="border-t border-slate-100" data-testid={`bet-row-${bet.id}`}>
      <td className="px-3 py-3 align-middle">
        <div className="text-slate-900 flex items-center gap-1">
          {isCombo && <Layers className="w-3.5 h-3.5" style={{ color: "var(--scommetti)" }} />}
          {bet.market_title}
        </div>
        <div className="text-slate-400 text-xs">Giornata {bet.matchday}</div>
        {isCombo && (
          <details className="text-xs text-slate-500 mt-1">
            <summary className="cursor-pointer text-slate-600">Dettagli {bet.legs?.length} selez.</summary>
            <ul className="mt-1 space-y-0.5 pl-3">
              {(bet.legs || []).map((l, i) => (
                <li key={i} className="flex items-center gap-1">
                  <span className={
                    l.leg_status === "won" ? "text-emerald-600" :
                    l.leg_status === "lost" ? "text-red-600" : "text-slate-500"
                  }>{l.leg_status === "won" ? "✓" : l.leg_status === "lost" ? "✗" : "•"}</span>
                  <span className="truncate">{l.market_title} → {l.option_label} <b>x{l.odds}</b></span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </td>
      <td className="px-3 py-3 text-slate-700 align-middle">{bet.option_label}</td>
      <td className="px-3 py-3 text-center align-middle">{bet.odds}</td>
      <td className="px-3 py-3 text-center align-middle">
        <span className="inline-flex items-center justify-center gap-1">
          <GufoIcon size={12} /> {bet.stake}
        </span>
      </td>
      <td className="px-3 py-3 text-center align-middle">
        <BetBadge status={bet.status} />
      </td>
      <td className="px-3 py-3 text-center align-middle font-black" style={{ color: bet.status === "won" ? "var(--brand)" : "var(--text-secondary)" }}>
        {bet.payout || "-"}
      </td>
      <td className="px-3 py-3 text-right align-middle">
        {bet.status === "open" && (
          <Button size="sm" variant="outline" onClick={() => onCashout(bet)}
                  className="border-slate-300 rounded-full text-xs" data-testid={`cashout-${bet.id}`}>
            <Timer className="w-3 h-3 mr-1" /> Cashout 80%
          </Button>
        )}
      </td>
    </tr>
  );
}

function CashRushPanel({ ranking, period, setPeriod, me }) {
  const podium = ranking.slice(0, 3);
  const rest = ranking.slice(3);
  return (
    <div className="space-y-4" data-testid="cash-rush-panel">
      <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 md:p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-full bg-amber-400 flex items-center justify-center shrink-0">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="display font-black text-lg text-amber-900">Cash Rush · Top Scommettitori</div>
            <p className="text-amber-900/80 text-sm mt-0.5">
              Classifica dei Gufi <b>netti vinti</b> alle scommesse. Il primo del mese si porta a casa
              un premio speciale!
            </p>
          </div>
          <div className="inline-flex bg-white/60 rounded-full p-1 border border-amber-200 w-full sm:w-auto justify-center">
            {[["week","Settimana"],["month","Mese"],["all","Sempre"]].map(([k, label]) => (
              <button key={k} onClick={() => setPeriod(k)}
                      data-testid={`cash-rush-period-${k}`}
                      className={`flex-1 sm:flex-none px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                        ${period === k ? "bg-amber-500 text-white" : "text-amber-800 hover:bg-white/60"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {ranking.length === 0 ? (
        <Empty text="Nessuna vincita registrata nel periodo. Sarai tu il primo?" />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {podium.map((r, i) => {
              const emojis = ["🥇", "🥈", "🥉"];
              const colors = ["#f59e0b", "#94a3b8", "#b45309"];
              return (
                <div key={r.user_id} className="rounded-2xl bg-white border-2 p-4 text-center"
                     style={{ borderColor: colors[i] + "80" }}
                     data-testid={`cash-rush-podium-${i + 1}`}>
                  <div className="text-4xl">{emojis[i]}</div>
                  <div className="display font-black text-lg mt-1 truncate" title={r.team_name || r.user_name}>
                    {r.team_name || r.user_name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {r.bets_won} scomm · quota media x{r.avg_odds_won}
                  </div>
                  <div className="mt-3 display font-black text-2xl flex items-center justify-center gap-1"
                       style={{ color: "var(--brand)" }}>
                    <GufoIcon size={18} /> +{r.net_gufi}
                  </div>
                </div>
              );
            })}
          </div>
          {rest.length > 0 && (
            <div className="card-surface rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="px-3 py-3 text-left w-12">#</th>
                    <th className="px-3 py-3 text-left">Fantallenatore</th>
                    <th className="px-3 py-3 text-center">Vinte</th>
                    <th className="px-3 py-3 text-center">Combo</th>
                    <th className="px-3 py-3 text-center">Quota media</th>
                    <th className="px-3 py-3 text-right">Net Gufi</th>
                  </tr>
                </thead>
                <tbody>
                  {rest.map((r) => (
                    <tr key={r.user_id}
                        className={`border-t border-slate-100 ${r.user_id === me ? "bg-amber-50" : ""}`}
                        data-testid={`cash-rush-row-${r.rank}`}>
                      <td className="px-3 py-3 font-black">{r.rank}</td>
                      <td className="px-3 py-3">{r.team_name || r.user_name}</td>
                      <td className="px-3 py-3 text-center">{r.bets_won}</td>
                      <td className="px-3 py-3 text-center">{r.combo_won || 0}</td>
                      <td className="px-3 py-3 text-center">x{r.avg_odds_won}</td>
                      <td className="px-3 py-3 text-right font-black" style={{ color: "var(--brand)" }}>
                        +{r.net_gufi}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AchievementsPanel({ data, meId }) {
  const [view, setView] = useState("mine"); // 'mine' | 'leaderboard'
  const [board, setBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(false);

  useEffect(() => {
    if (view !== "leaderboard" || board) return;
    setBoardLoading(true);
    api.get("/public/achievements/leaderboard?limit=32")
      .then(({ data }) => setBoard(data?.leaderboard || []))
      .catch(() => setBoard([]))
      .finally(() => setBoardLoading(false));
  }, [view]);

  if (!data && view === "mine") return <Empty text="Caricamento trofei..." />;
  const { unlocked = [], locked = [], stats = {}, total = 0, unlocked_count = 0 } = data || {};
  const tierColors = {
    legend: { bg: "linear-gradient(135deg, #fbbf24, #f59e0b, #dc2626)", ring: "#f59e0b", txt: "#78350f" },
    gold:   { bg: "linear-gradient(135deg, #fde68a, #f59e0b)",           ring: "#f59e0b", txt: "#78350f" },
    silver: { bg: "linear-gradient(135deg, #e2e8f0, #94a3b8)",           ring: "#94a3b8", txt: "#334155" },
    bronze: { bg: "linear-gradient(135deg, #fed7aa, #c2410c)",           ring: "#c2410c", txt: "#7c2d12" },
  };
  return (
    <div className="space-y-6" data-testid="achievements-panel">
      {/* View switcher */}
      <div className="inline-flex bg-slate-50 rounded-full p-1 border border-slate-200" data-testid="trofei-view-switcher">
        <button onClick={() => setView("mine")}
                data-testid="trofei-view-mine"
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5
                  ${view === "mine" ? "bg-amber-500 text-white" : "text-slate-600 hover:bg-white/60"}`}>
          <Award className="w-3.5 h-3.5" /> I miei
        </button>
        <button onClick={() => setView("leaderboard")}
                data-testid="trofei-view-leaderboard"
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5
                  ${view === "leaderboard" ? "bg-amber-500 text-white" : "text-slate-600 hover:bg-white/60"}`}>
          <Crown className="w-3.5 h-3.5" /> Classifica Trofei
        </button>
      </div>

      {view === "mine" ? (
        <>
          {/* Summary card */}
          <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: "linear-gradient(135deg, #fbbf24, #dc2626)" }}>
                <Award className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="display font-black text-xl text-amber-900">I tuoi Trofei</div>
                <div className="text-sm text-amber-900/80">
                  <b>{unlocked_count}</b> / {total} sbloccati · Bet piazzate: <b>{stats.bets_placed}</b> · Vinte: <b>{stats.bets_won}</b> · Net Gufi: <b>{stats.total_net}</b>
                </div>
                <div className="mt-2 h-2 rounded-full bg-white/60 overflow-hidden">
                  <div className="h-full transition-all duration-500"
                       style={{ width: `${(unlocked_count / Math.max(total, 1)) * 100}%`,
                                background: "linear-gradient(90deg, #fbbf24, #dc2626)" }} />
                </div>
              </div>
            </div>
          </div>

          {unlocked.length > 0 && (
            <div>
              <div className="display font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" /> Sbloccati ({unlocked.length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {unlocked.map((a) => (
                  <BadgeCard key={a.id} ach={a} tierColors={tierColors} unlocked />
                ))}
              </div>
            </div>
          )}

          {locked.length > 0 && (
            <div>
              <div className="display font-bold text-lg text-slate-500 mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5" /> Da sbloccare ({locked.length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {locked.map((a) => (
                  <BadgeCard key={a.id} ach={a} tierColors={tierColors} unlocked={false} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <TrofeiLeaderboard rows={board} loading={boardLoading} meId={meId} />
      )}
    </div>
  );
}

function TrofeiLeaderboard({ rows, loading, meId }) {
  if (loading) return <Empty text="Carico classifica..." />;
  if (!rows || rows.length === 0) return <Empty text="Ancora nessun trofeo sbloccato dalla community." />;
  const maxScore = Math.max(...rows.map((r) => r.score || 0), 1);
  return (
    <div className="card-surface rounded-2xl overflow-hidden" data-testid="trofei-leaderboard">
      <div className="px-5 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center gap-3">
        <Crown className="w-6 h-6 text-amber-500" />
        <div>
          <div className="display font-black text-lg text-amber-900">Classifica Trofei</div>
          <div className="text-xs text-amber-900/80">Punteggio pesato: bronzo=1, argento=2, oro=3, leggenda=4.</div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => {
          const isMe = meId && r.user_id === meId;
          const pct = ((r.score || 0) / maxScore) * 100;
          const rankColor = r.rank === 1 ? "#f59e0b" : r.rank === 2 ? "#94a3b8" : r.rank === 3 ? "#b45309" : "#cbd5e1";
          return (
            <div key={r.user_id}
                 className={`px-4 py-3 flex items-center gap-3 ${isMe ? "bg-amber-50/60" : ""}`}
                 data-testid={`trofei-lb-row-${r.user_id}`}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black shrink-0 text-white"
                   style={{ background: rankColor }}>{r.rank}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="display font-black text-sm truncate">{r.team_name || r.user_name || "—"}</div>
                  {isMe && <span className="text-[9px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">tu</span>}
                  {r.girone && <GironeBadge girone={r.girone} size={14} />}
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full transition-all"
                       style={{ width: `${pct}%`,
                                background: "linear-gradient(90deg, #fbbf24, #dc2626)" }} />
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="display font-black text-lg tabular-nums text-slate-900">{r.score}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{r.unlocked}/{r.total}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BadgeCard({ ach, tierColors, unlocked }) {
  const t = tierColors[ach.tier] || tierColors.bronze;
  return (
    <div className={`rounded-2xl p-4 border-2 text-center transition-all
      ${unlocked ? "bg-white shadow-md hover:shadow-lg" : "bg-slate-50 border-dashed border-slate-200"}`}
      style={{ borderColor: unlocked ? t.ring : undefined }}
      data-testid={`badge-${ach.id}`}
      title={ach.description}>
      <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl
        ${unlocked ? "" : "grayscale opacity-40"}`}
        style={{ background: unlocked ? t.bg : "#e5e7eb" }}>
        {ach.icon}
      </div>
      <div className={`display font-black text-sm mt-2 ${unlocked ? "" : "text-slate-500"}`}
           style={{ color: unlocked ? t.txt : undefined }}>
        {ach.name}
      </div>
      <div className="text-[10px] uppercase tracking-widest mt-0.5"
           style={{ color: unlocked ? t.ring : "#94a3b8" }}>
        {ach.tier}
      </div>
      <div className="text-xs text-slate-500 mt-1 line-clamp-2">
        {ach.description}
      </div>
      {!unlocked && (
        <div className="mt-2">
          <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full bg-slate-400 transition-all"
                 style={{ width: `${ach.progress?.pct || 0}%` }} />
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {ach.progress?.current} / {ach.progress?.target}
          </div>
        </div>
      )}
    </div>
  );
}

function KickoffCountdown({ kickoffAt, className = "" }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!kickoffAt) return;
    const ko = new Date(kickoffAt).getTime();
    if (isNaN(ko)) return;
    let cancelled = false;
    let timeoutId;
    const schedule = () => {
      if (cancelled) return;
      const diff = ko - Date.now();
      // 1 s se < 5 min, altrimenti 30 s. Zero → non ripianificare.
      const delay = diff <= 0 ? 0 : diff <= 5 * 60 * 1000 ? 1000 : 30000;
      if (delay === 0) { setNow(Date.now()); return; }
      timeoutId = setTimeout(() => { setNow(Date.now()); schedule(); }, delay);
    };
    schedule();
    return () => { cancelled = true; if (timeoutId) clearTimeout(timeoutId); };
  }, [kickoffAt]);
  if (!kickoffAt) return null;
  const ko = new Date(kickoffAt).getTime();
  if (isNaN(ko)) return null;
  const ms = ko - now;
  if (ms <= 0) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold bg-red-100 text-red-700 ${className}`}
            title="Le partite sono iniziate, puntate chiuse"
            data-testid="kickoff-countdown-closed">
        <Lock className="w-2.5 h-2.5" /> Chiusa
      </span>
    );
  }
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  let text;
  if (days >= 1) text = `${days}g ${hours}h`;
  else if (hours >= 1) text = `${hours}h ${String(minutes).padStart(2, "0")}min`;
  else if (minutes >= 5) text = `${minutes}min`;
  else text = `${minutes}:${String(seconds).padStart(2, "0")}`;
  const urgent = ms <= 60 * 60 * 1000; // < 1h
  const critical = ms <= 10 * 60 * 1000; // < 10min
  const bg = critical ? "bg-red-100 text-red-700" : urgent ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold ${bg} ${critical ? "animate-pulse" : ""} ${className}`}
          title={`Chiusura scommesse: ${new Date(kickoffAt).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`}
          data-testid="kickoff-countdown">
      <Timer className="w-2.5 h-2.5" />
      Chiude in {text}
    </span>
  );
}

function MarketCard({ market, onBet, disabled, comboMode, comboSelected }) {
  const winning = market.status === "settled" ? market.winning_option_id : null;
  const insight = getBookmakerInsight(market);
  const isDynamic = market.dynamic && market.options?.some((o) => o.base_odds && o.base_odds !== o.odds);
  return (
    <div className="card-surface rounded-2xl p-5 card-lift" data-testid={`market-card-${market.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5 flex-wrap">
            Giornata {market.matchday}
            {isDynamic && (
              <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold"
                    title="Quote aggiornate in base al volume di puntate">
                <Zap className="w-2.5 h-2.5" /> LIVE
              </span>
            )}
            {market.status === "open" && market.kickoff_at && (
              <KickoffCountdown kickoffAt={market.kickoff_at} />
            )}
          </div>
          <div className="display font-bold text-lg">{market.title}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {insight && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold"
                  style={{ background: `${insight.color}22`, color: insight.color }}
                  data-testid={`market-insight-${market.id}`}
                  title={insight.tooltip}>
              {insight.emoji} {insight.label}
            </span>
          )}
          <MarketBadge status={market.status} />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2">
        {market.options.map((opt) => {
          const isSelected = comboMode && comboSelected === opt.id;
          const moved = opt.base_odds && Math.abs((opt.base_odds - opt.odds) / opt.base_odds) >= 0.05;
          const trend = opt.base_odds ? (opt.odds < opt.base_odds ? "▼" : opt.odds > opt.base_odds ? "▲" : "") : "";
          return (
            <button
              key={opt.id}
              disabled={disabled || market.status !== "open"}
              onClick={() => onBet && onBet(opt)}
              data-testid={`option-${opt.id}`}
              className={`flex items-center justify-between rounded-xl px-4 py-3 border transition-all
                ${winning === opt.id ? "border-[var(--brand)]/70 bg-[var(--brand)]/10" : ""}
                ${isSelected ? "border-[var(--scommetti)] bg-[var(--scommetti)]/10 ring-2 ring-[var(--scommetti)]/40" : "border-slate-200 hover:border-[var(--scommetti)]/60 hover:bg-slate-50"}
                disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              <span className="text-slate-900 text-sm text-left flex items-center gap-2">
                {winning === opt.id && <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--brand)" }} />}
                {isSelected && <Layers className="w-3.5 h-3.5" style={{ color: "var(--scommetti)" }} />}
                {opt.label}
              </span>
              <span className="display font-black flex items-center gap-1" style={{ color: "var(--scommetti)" }}>
                {moved && (
                  <span className={`text-[10px] ${opt.odds < opt.base_odds ? "text-red-500" : "text-emerald-600"}`}
                        title={`Quota iniziale: x${opt.base_odds}`}>{trend}</span>
                )}
                x{opt.odds}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MarketBadge({ status }) {
  const map = {
    open: { label: "Live", color: "var(--brand)" },
    closed: { label: "In attesa", color: "#a1a1aa" },
    settled: { label: "Liquidato", color: "var(--scommetti)" },
  };
  const s = map[status] || map.closed;
  return (
    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
          style={{ background: `${s.color}22`, color: s.color }}>{s.label}</span>
  );
}

function BetBadge({ status }) {
  const map = {
    open: { label: "Aperta", color: "var(--brand)" },
    won: { label: "Vinta", color: "var(--brand)" },
    lost: { label: "Persa", color: "#ef4444" },
    cashed_out: { label: "Cashout", color: "#a1a1aa" },
  };
  const s = map[status] || map.open;
  return <Badge className="border-0" style={{ background: `${s.color}22`, color: s.color }}>{s.label}</Badge>;
}

function Empty({ text }) {
  return <div className="card-surface rounded-2xl p-8 text-center text-slate-500">{text}</div>;
}

function getBookmakerInsight(market) {
  const opts = market.options || [];
  if (opts.length !== 3) return null;
  const home = opts.find((o) => (o.label || "").startsWith("1 "));
  const away = opts.find((o) => (o.label || "").startsWith("2 "));
  if (!home || !away || !home.odds || !away.odds) return null;
  const oh = Number(home.odds), oa = Number(away.odds);
  const gap = Math.abs(oh - oa) / Math.min(oh, oa);
  const favHome = oh < oa;
  if (gap >= 0.30) {
    return favHome
      ? { label: "Favorita casa", emoji: "★", color: "#0ea5e9", tooltip: `Casa nettamente favorita (${oh} vs ${oa})` }
      : { label: "Favorita ospite", emoji: "★", color: "#a855f7", tooltip: `Ospite nettamente favorita (${oa} vs ${oh})` };
  }
  if (gap >= 0.15) {
    return favHome
      ? { label: "Leggera casa", emoji: "▲", color: "#22c55e", tooltip: `Leggero favorito casa (${oh} vs ${oa})` }
      : { label: "Leggera ospite", emoji: "▲", color: "#f97316", tooltip: `Leggero favorito ospite (${oa} vs ${oh})` };
  }
  return { label: "Equilibrio", emoji: "⚖", color: "#64748b", tooltip: `Match equilibrato (${oh} vs ${oa})` };
}
